/**
 * As funções da linguagem — exatamente as do card, e nenhuma a mais.
 *
 * O escopo é **mínimo deliberado**: é o que 90% dos templates financeiros usam. Acrescentar
 * função é barato aqui e caro depois — cada uma vira contrato com o documento de quem a
 * escreveu, e remover uma quebra fórmula gravada.
 *
 * ## Duas coisas que valem para todas
 *
 * **`hoje` entra por INJEÇÃO, nunca por `Date.now()` direto.** Uma função que lê o relógio do
 * sistema é uma função que não se testa: o mesmo teste passa hoje e falha em janeiro. O
 * contexto carrega o instante, o avaliador o repassa, e o teste declara qual dia é.
 *
 * **Aridade errada é `argumentos`, não `sintaxe`.** São erros diferentes para o usuário:
 * `round(1,2,3)` está escrito corretamente e chamado errado, e mandar procurar erro de
 * digitação faria perder tempo no lugar errado.
 */

import { erro } from './erros';
import {
  comoBooleano, comoNumero, comoTexto, ehVazio, normalizar, VAZIO, type Valor,
} from './valores';
import { formatar, type FormatoDeSaida } from './formato';

export interface ContextoDeFuncoes {
  /** O "agora" — injetado, para que o teste possa fixá-lo. Epoch ms. */
  agora: number;
  /** Locale de formatação. `pt-BR` em toda instalação nossa; parametrizado para o teste. */
  locale: string;
}

type Implementacao = (args: Valor[], ctx: ContextoDeFuncoes) => Valor;

interface Assinatura {
  minimo: number;
  maximo: number;
  /** Se `true`, a função É CHAMADA mesmo com argumento vazio (ex.: `isEmpty`, `coalesce`). */
  aceitaVazio: boolean;
  impl: Implementacao;
}

const UM_DIA = 86_400_000;

/** Epoch ms → partes da data em UTC.
 *
 *  ⚠️ **UTC de propósito.** O Affine guarda data como número, e o mesmo documento é aberto de
 *  fusos diferentes. Ler com `getMonth()` (local) faria `month(prop("Competência"))` responder
 *  `12` para quem está em São Paulo e `1` para quem está em Lisboa **sobre a mesma linha** — e
 *  um DRE fecharia diferente conforme quem abriu. A escolha é ser igual em todo lugar.
 */
function partes(ms: number) {
  const d = new Date(ms);
  return { ano: d.getUTCFullYear(), mes: d.getUTCMonth() + 1, dia: d.getUTCDate() };
}

function exigirData(v: Valor, onde: string): number {
  const n = comoNumero(v, onde);
  // Uma data plausível está entre 1970 e ~5138. Recusar o resto pega o erro clássico de
  // passar segundos onde se espera milissegundos — que produziria "1970" em toda linha.
  if (!Number.isFinite(n)) throw erro('tipo', `${onde}: data inválida`);
  return n;
}

export const FUNCOES: Record<string, Assinatura> = {
  // ── condicional ───────────────────────────────────────────────────────────────────────
  // ⚠️ `if` recebe os três argumentos JÁ avaliados. Não há curto-circuito, e isso é
  // consequência do desenho: um `if` preguiçoso exigiria que a função recebesse nós da árvore
  // em vez de valores, e o único ganho seria não avaliar um ramo que é barato. O que NÃO se
  // perde é correção — `if(x != 0, y/x, 0)` continua funcionando porque divisão por zero só
  // lança quando o denominador É zero, e nesse caso o ramo escolhido é o outro. Ver a nota em
  // `eval.ts`, que trata essa combinação explicitamente.
  if: {
    minimo: 3, maximo: 3, aceitaVazio: true,
    impl: ([c, a, b]) => (comoBooleano(ehVazio(c) ? false : c, 'if') ? a : b),
  },

  // ── lógica ────────────────────────────────────────────────────────────────────────────
  and: {
    minimo: 2, maximo: 8, aceitaVazio: true,
    impl: args => args.every(v => !ehVazio(v) && comoBooleano(v, 'and')),
  },
  or: {
    minimo: 2, maximo: 8, aceitaVazio: true,
    impl: args => args.some(v => !ehVazio(v) && comoBooleano(v, 'or')),
  },
  not: {
    minimo: 1, maximo: 1, aceitaVazio: true,
    impl: ([v]) => (ehVazio(v) ? true : !comoBooleano(v, 'not')),
  },

  // ── número ────────────────────────────────────────────────────────────────────────────
  round: {
    minimo: 1, maximo: 2, aceitaVazio: false,
    impl: ([x, casas]) => {
      const n = comoNumero(x, 'round');
      const c = casas === undefined ? 0 : Math.trunc(comoNumero(casas, 'round'));
      if (c < 0 || c > 15) throw erro('argumentos', 'round: casas decimais entre 0 e 15');
      // ⚠️ `Math.round(n * 10**c) / 10**c` erra em casos como `1.005` por causa do binário.
      // O caminho por string é o que a planilha faz, e é o que o usuário espera ver.
      const fator = 10 ** c;
      const arredondado = Math.round((n * fator + (n >= 0 ? Number.EPSILON : -Number.EPSILON)
        * Math.abs(n * fator))) / fator;
      return Object.is(arredondado, -0) ? 0 : arredondado;
    },
  },
  abs: { minimo: 1, maximo: 1, aceitaVazio: false, impl: ([x]) => Math.abs(comoNumero(x, 'abs')) },
  min: {
    minimo: 2, maximo: 8, aceitaVazio: false,
    impl: args => Math.min(...args.map(v => comoNumero(v, 'min'))),
  },
  max: {
    minimo: 2, maximo: 8, aceitaVazio: false,
    impl: args => Math.max(...args.map(v => comoNumero(v, 'max'))),
  },

  // ── data ──────────────────────────────────────────────────────────────────────────────
  today: {
    minimo: 0, maximo: 0, aceitaVazio: true,
    // Meia-noite UTC do dia corrente — para que `dateBetween(today(), x, "days")` devolva
    // dias inteiros em vez de fração da hora em que alguém abriu o documento.
    impl: (_a, ctx) => Math.floor(ctx.agora / UM_DIA) * UM_DIA,
  },
  dateBetween: {
    minimo: 2, maximo: 3, aceitaVazio: false,
    impl: ([a, b, unidade]) => {
      const inicio = exigirData(a, 'dateBetween');
      const fim = exigirData(b, 'dateBetween');
      const u = unidade === undefined ? 'days' : comoTexto(unidade).toLowerCase();
      const diffMs = fim - inicio;
      switch (u) {
        case 'days': case 'dias': return Math.trunc(diffMs / UM_DIA);
        case 'weeks': case 'semanas': return Math.trunc(diffMs / (UM_DIA * 7));
        case 'months': case 'meses': {
          const x = partes(inicio); const y = partes(fim);
          // Meses de calendário, não `dias/30`. "3 meses de atraso" tem significado no
          // vencimento de uma fatura, e `dias/30` erraria em fevereiro toda vez.
          let meses = (y.ano - x.ano) * 12 + (y.mes - x.mes);
          if (y.dia < x.dia) meses -= 1;
          return meses;
        }
        case 'years': case 'anos': {
          const x = partes(inicio); const y = partes(fim);
          let anos = y.ano - x.ano;
          if (y.mes < x.mes || (y.mes === x.mes && y.dia < x.dia)) anos -= 1;
          return anos;
        }
        default:
          throw erro('argumentos',
            `dateBetween: unidade "${u}" desconhecida — use days, weeks, months ou years`);
      }
    },
  },
  month: { minimo: 1, maximo: 1, aceitaVazio: false,
    impl: ([d]) => partes(exigirData(d, 'month')).mes },
  year: { minimo: 1, maximo: 1, aceitaVazio: false,
    impl: ([d]) => partes(exigirData(d, 'year')).ano },

  // ── texto ─────────────────────────────────────────────────────────────────────────────
  // A exceção declarada da propagação de vazio (ver `valores.ts`): aqui vazio vira "".
  concat: {
    minimo: 1, maximo: 8, aceitaVazio: true,
    impl: args => args.map(comoTexto).join(''),
  },
  format: {
    minimo: 2, maximo: 2, aceitaVazio: false,
    impl: ([x, f], ctx) => formatar(comoNumero(x, 'format'),
      comoTexto(f) as FormatoDeSaida, ctx.locale),
  },

  // ── vazio ─────────────────────────────────────────────────────────────────────────────
  isEmpty: { minimo: 1, maximo: 1, aceitaVazio: true, impl: ([v]) => ehVazio(v) },
  coalesce: {
    minimo: 2, maximo: 8, aceitaVazio: true,
    impl: args => args.find(v => !ehVazio(v)) ?? VAZIO,
  },
};

export function chamar(nome: string, argumentos: Valor[], ctx: ContextoDeFuncoes): Valor {
  const assinatura = FUNCOES[nome];
  if (!assinatura) {
    throw erro('funcao-ausente',
      `"${nome}" não é uma função. Disponíveis: ${Object.keys(FUNCOES).sort().join(', ')}`);
  }
  if (argumentos.length < assinatura.minimo || argumentos.length > assinatura.maximo) {
    const esperado = assinatura.minimo === assinatura.maximo
      ? `${assinatura.minimo}`
      : `entre ${assinatura.minimo} e ${assinatura.maximo}`;
    throw erro('argumentos',
      `${nome} espera ${esperado} argumento(s) e recebeu ${argumentos.length}`);
  }
  // Vazio se propaga por default (ver `valores.ts`); as funções que sabem lidar com ele
  // declaram `aceitaVazio`.
  if (!assinatura.aceitaVazio && argumentos.some(ehVazio)) return VAZIO;
  return normalizar(assinatura.impl(argumentos, ctx));
}
